import {formatPriceNl} from "./toolKit";

export const TARIFF_TYPES = {
    EXPONENTIAL: "Exponential",
    ALL_WEEK: "All Week",
    HOLIDAY: "Holiday",
    SATURDAY_KB: "Saturday-KB",
    WEEKEND_KB: "Weekend-KB",
}

const TARIFF_PRIORITY = {
    [TARIFF_TYPES.HOLIDAY]: 0,
    [TARIFF_TYPES.SATURDAY_KB]: 1,
    [TARIFF_TYPES.WEEKEND_KB]: 2,
    [TARIFF_TYPES.ALL_WEEK]: 3,
    [TARIFF_TYPES.EXPONENTIAL]: 4,
}

const parseDateOnly = (value) => {
    const rawValue = String(value || "").trim()
    if (!rawValue) return null

    const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) return null
    return date
}

export const stripDateTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const normalizeHolidayRangeForYear = (holiday, year) => {
    const from = parseDateOnly(holiday?.from)
    const until = parseDateOnly(holiday?.until || holiday?.from)
    if (!from || !until || !Number.isFinite(year)) return null

    const rangeStart = new Date(year, from.getMonth(), from.getDate())
    const rangeEnd = new Date(year, until.getMonth(), until.getDate())

    if (rangeEnd.getTime() < rangeStart.getTime()) {
        rangeEnd.setFullYear(rangeEnd.getFullYear() + 1)
    }

    return {from: rangeStart, until: rangeEnd}
}

export const getTariffType = (categoryOptionOrType) => {
    if (typeof categoryOptionOrType === "string") return categoryOptionOrType.trim()
    return String(categoryOptionOrType?.enabled_for || "").trim()
}

export const getTariffName = (categoryOption) => {
    return String(categoryOption?.tariff_name || "").trim()
}

export const isHolidayTariff = (categoryOptionOrType) => {
    return getTariffType(categoryOptionOrType) === TARIFF_TYPES.HOLIDAY
}

export const isSaturdayTariff = (categoryOptionOrType) => {
    return getTariffType(categoryOptionOrType) === TARIFF_TYPES.SATURDAY_KB
}

export const isWeekendTariff = (categoryOptionOrType) => {
    return getTariffType(categoryOptionOrType) === TARIFF_TYPES.WEEKEND_KB
}

export const isSpecialTariffType = (type) => {
    return [
        TARIFF_TYPES.HOLIDAY,
        TARIFF_TYPES.SATURDAY_KB,
        TARIFF_TYPES.WEEKEND_KB,
    ].includes(getTariffType(type))
}

export const getCategoryOptionDurationInDays = (categoryOption) => {
    const durationUnit = String(categoryOption?.duration?.unit || "").trim().toLowerCase()
    const durationValue = Number(categoryOption?.duration?.value)
    if (!Number.isFinite(durationValue) || durationValue <= 0) return null
    if (durationUnit === "day") return durationValue
    if (durationUnit === "week") return durationValue * 7
    return null
}

export const getCategoryOptionPrice = (categoryOption, showPricesInclVat = true) => {
    const preferredPrice = Number(
        showPricesInclVat ? categoryOption?.price_including_vat : categoryOption?.price_excluding_vat
    )
    if (Number.isFinite(preferredPrice)) return preferredPrice

    const fallbackPrice = Number(
        showPricesInclVat ? categoryOption?.price_excluding_vat : categoryOption?.price_including_vat
    )
    return Number.isFinite(fallbackPrice) ? fallbackPrice : null
}

export const formatCategoryOptionLabel = (categoryOption, showPricesInclVat = true) => {
    const price = getCategoryOptionPrice(categoryOption, showPricesInclVat)
    const priceText = Number.isFinite(price) ? formatPriceNl(price) : "--"
    const compactPriceText = priceText.endsWith(",00") ? priceText.slice(0, -3) : priceText

    const durationValue = Number(categoryOption?.duration?.value)
    const durationUnit = String(categoryOption?.duration?.unit || "").trim()
    const hasDuration = Number.isFinite(durationValue) && durationValue > 0 && durationUnit
    const durationText = hasDuration ? `${durationValue} ${durationUnit}` : ""

    const freeKm = Number(categoryOption?.free_km)
    const normalizedFreeKm = Number.isFinite(freeKm) ? Math.max(0, Math.round(freeKm)) : null
    const freeKmLabel = normalizedFreeKm !== null
        ? (normalizedFreeKm >= 100000 ? "∞" : String(normalizedFreeKm))
        : null
    const freeKmText = freeKmLabel ? `${freeKmLabel} KM` : "KM"

    if (isWeekendTariff(categoryOption)) {
        return `€ ${compactPriceText} / weekend ${freeKmLabel || "0"} km`
    }
    if (isSaturdayTariff(categoryOption)) {
        return `€ ${compactPriceText} / zaterdag met ${freeKmLabel || "0"} km`
    }
    if (isHolidayTariff(categoryOption)) {
        const holidayName = getTariffName(categoryOption) || "feestdag"
        return `€ ${compactPriceText} / ${holidayName} ${freeKmLabel || "0"} km`
    }

    return `€ ${priceText}${durationText ? ` / ${durationText}` : ""} ${freeKmText}`.trim()
}

export const sortCategoryOptions = (categoryOptions, showPricesInclVat = true) => {
    if (!Array.isArray(categoryOptions)) return []

    return categoryOptions
        .filter((option) => option && option.id !== undefined && option.id !== null)
        .slice()
        .sort((left, right) => {
            const leftEnabledFor = getTariffType(left)
            const rightEnabledFor = getTariffType(right)

            const leftPriority = Object.prototype.hasOwnProperty.call(TARIFF_PRIORITY, leftEnabledFor)
                ? TARIFF_PRIORITY[leftEnabledFor]
                : 99
            const rightPriority = Object.prototype.hasOwnProperty.call(TARIFF_PRIORITY, rightEnabledFor)
                ? TARIFF_PRIORITY[rightEnabledFor]
                : 99

            if (leftPriority !== rightPriority) return leftPriority - rightPriority

            if (leftEnabledFor === TARIFF_TYPES.ALL_WEEK || leftEnabledFor === TARIFF_TYPES.HOLIDAY) {
                const leftPrice = getCategoryOptionPrice(left, showPricesInclVat)
                const rightPrice = getCategoryOptionPrice(right, showPricesInclVat)

                const safeLeftPrice = Number.isFinite(leftPrice) ? leftPrice : Number.POSITIVE_INFINITY
                const safeRightPrice = Number.isFinite(rightPrice) ? rightPrice : Number.POSITIVE_INFINITY

                if (safeLeftPrice !== safeRightPrice) return safeLeftPrice - safeRightPrice
            }

            const leftId = Number(left?.id)
            const rightId = Number(right?.id)
            if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId

            return 0
        })
}

export const getHolidayRangeForReferenceDate = (categoryOption, referenceDate = new Date()) => {
    if (!isHolidayTariff(categoryOption)) return null

    const holiday = categoryOption?.holiday
    const reference = stripDateTime(referenceDate)
    if (!holiday || !reference) return null

    if (!holiday.repeats_yearly) {
        const from = parseDateOnly(holiday.from)
        const until = parseDateOnly(holiday.until || holiday.from)
        if (!from || !until) return null
        return {from, until: until.getTime() < from.getTime() ? from : until}
    }

    const currentYearRange = normalizeHolidayRangeForYear(holiday, reference.getFullYear())
    const nextYearRange = normalizeHolidayRangeForYear(holiday, reference.getFullYear() + 1)
    const previousYearRange = normalizeHolidayRangeForYear(holiday, reference.getFullYear() - 1)
    const ranges = [previousYearRange, currentYearRange, nextYearRange].filter(Boolean)

    return ranges.find((range) => (
        reference.getTime() >= range.from.getTime() &&
        reference.getTime() <= range.until.getTime()
    )) || ranges.find((range) => reference.getTime() <= range.until.getTime()) || null
}

export const isDateWithinHolidayTariff = (date, categoryOption) => {
    const normalizedDate = stripDateTime(date)
    const range = getHolidayRangeForReferenceDate(categoryOption, normalizedDate || new Date())
    if (!normalizedDate || !range) return false

    return normalizedDate.getTime() >= range.from.getTime() &&
        normalizedDate.getTime() <= range.until.getTime()
}

export const getNextHolidayTariffRange = (categoryOption, fromDate = new Date()) => {
    const reference = stripDateTime(fromDate)
    if (!reference) return null

    const sameOrNextRange = getHolidayRangeForReferenceDate(categoryOption, reference)
    if (!sameOrNextRange) return null

    if (reference.getTime() <= sameOrNextRange.until.getTime()) return sameOrNextRange
    return null
}

export const isSameHolidayRule = (categoryOption, specialPriceRule) => {
    if (!isHolidayTariff(categoryOption) || !isHolidayTariff(specialPriceRule)) return false

    const categoryId = Number(categoryOption?.id)
    const ruleId = Number(specialPriceRule?.id)
    if (Number.isFinite(categoryId) && Number.isFinite(ruleId) && categoryId === ruleId) return true

    const categoryName = getTariffName(categoryOption)
    const ruleName = getTariffName(specialPriceRule)
    if (categoryName && ruleName && categoryName === ruleName) return true

    const categoryHoliday = categoryOption?.holiday || {}
    const ruleHoliday = specialPriceRule?.holiday || {}
    return Boolean(
        categoryHoliday.from &&
        ruleHoliday.from &&
        String(categoryHoliday.from) === String(ruleHoliday.from) &&
        String(categoryHoliday.until || categoryHoliday.from) === String(ruleHoliday.until || ruleHoliday.from)
    )
}
